import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Icon from '@/components/AppIcon';
import { Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter, DialogTitle } from '@/components/ui/Dialog';
import AlertDialog from '@/components/ui/AlertDialog';
import { useToast, ToastRenderer } from '@/hooks/useToast';
import { getAccrualRules, saveAccrualRule, updateAccrualRule, deleteAccrualRule, getCompanyLeaveTypes, getAccrualRuleEligibleCount } from '../../../../../api/Attendance/leaves';
import { getDepartments, getBranches } from '../../../../../api/General/Company';
import { AccrualFrequency, EmploymentType, LeavePayType } from '../../../../../models/attendance';
import type { LeavePolicyView, AccrualRuleView, AccrualRuleInput, LeaveTypeView, PayTierView } from '../../../../../models/attendance';
import type { Department, Branch } from '../../../../../models/general';
import { getApiErrorMessage } from '../../../../../utils/apiErrors';

interface EntitlementsAccrualsTabProps {
  currentPolicy?: LeavePolicyView;
}

/** Local form state for an accrual rule being created/edited */
interface RuleFormState {
  id?: number;
  name: string;
  roleLevels: string;
  departmentIds: string;
  branchIds: string;
  minYearsOfService: number;
  maxYearsOfService: number;
  employmentTypes: string;
  ftePercentage: number;
  isActive: boolean;
  probationRestricted: boolean;
  effectiveFrom: string;
  entitlements: EntitlementFormRow[];
}

interface PayTierFormRow {
  fromDay: number;
  toDay: number;
  payType: LeavePayType | '';
}

interface EntitlementFormRow {
  leaveTypeId: number;
  entitlementDays: number;
  accrualRate: string;
  accrualFrequency: AccrualFrequency | '';
  eligibilityMonths: number;
  maxAccumulationCap: number | '';
  payTiers: PayTierFormRow[];
}

const FREQUENCY_PERIODS: Record<string, number> = {
  [AccrualFrequency.Monthly]: 12,
  [AccrualFrequency.Quarterly]: 4,
  [AccrualFrequency.SemiAnnually]: 2,
  [AccrualFrequency.Annually]: 1,
  [AccrualFrequency.OnHireDate]: 1,
};

/** Compute a display string like "2.50 days/month" from entitlement days and frequency */
function computeRateDisplay(entitlementDays: number, frequency?: AccrualFrequency | '' | null, t?: (key: string) => string): string {
  if (!frequency || !FREQUENCY_PERIODS[frequency]) return '';
  const periods = FREQUENCY_PERIODS[frequency];
  const rate = (entitlementDays / periods).toFixed(2);
  const labelKey = frequency === AccrualFrequency.Monthly ? 'month'
    : frequency === AccrualFrequency.Quarterly ? 'quarter'
    : frequency === AccrualFrequency.SemiAnnually ? 'half-year'
    : frequency === AccrualFrequency.Annually ? 'year'
    : 'hire-date';
  const label = t ? t(labelKey) : labelKey;
  return `${rate}/${label}`;
}

const EntitlementsAccrualsTab: React.FC<EntitlementsAccrualsTabProps> = ({ currentPolicy }) => {
  const { t } = useTranslation('leaves');

  const [accrualRules, setAccrualRules] = useState<AccrualRuleView[]>([]);
  const [leaveTypesList, setLeaveTypesList] = useState<LeaveTypeView[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [eligibleCount, setEligibleCount] = useState<number | null>(null);

  const [showRuleModal, setShowRuleModal] = useState(false);
  const [editingRule, setEditingRule] = useState<AccrualRuleView | null>(null);
  const [formState, setFormState] = useState<RuleFormState | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const companyId = useSelector((state: any) => state.user?.company?.id);
  const [departmentsList, setDepartmentsList] = useState<Department[]>([]);
  const [branchesList, setBranchesList] = useState<Branch[]>([]);

  const { toast, showToast, dismissToast } = useToast();
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; ruleId: number | null }>({ open: false, ruleId: null });
  const [tierWarningOpen, setTierWarningOpen] = useState(false);

  const employmentTypeOptions = [
    { value: EmploymentType.FullTime, label: t('Full-time') },
    { value: EmploymentType.PartTime, label: t('Part-time') },
    { value: EmploymentType.Contract, label: t('Contract') },
    { value: EmploymentType.Executive, label: t('Executive') }
  ];

  const frequencyOptions = [
    { value: AccrualFrequency.Monthly, label: t('Monthly') },
    { value: AccrualFrequency.Quarterly, label: t('Quarterly') },
    { value: AccrualFrequency.SemiAnnually, label: t('Semi-Annually') },
    { value: AccrualFrequency.Annually, label: t('Annually') },
    { value: AccrualFrequency.OnHireDate, label: t('On Hire Date') },
    { value: AccrualFrequency.Never, label: t('Never') }
  ];

  const payTypeOptions = [
    { value: LeavePayType.Paid, label: t('Paid') },
    { value: LeavePayType.HalfPaid, label: t('Half Paid') },
    { value: LeavePayType.Unpaid, label: t('Unpaid') }
  ];

  // Track which entitlement rows have pay tiers expanded
  const [expandedPayTiers, setExpandedPayTiers] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (currentPolicy?.id) {
      loadAccrualRules();
      loadLeaveTypes();
      loadEligibleCount();
    }
  }, [currentPolicy?.id]);

  useEffect(() => {
    if (companyId) {
      loadDepartments();
      loadBranches();
    }
  }, [companyId]);

  const loadAccrualRules = async () => {
    if (!currentPolicy?.id) return;
    setIsLoading(true);
    try {
      const result = await getAccrualRules(currentPolicy.id);
      if (result?.errors?.length) {
        setError(getApiErrorMessage(result, t, t('Failed to load accrual rules')));
      } else {
        setError(null);
        setAccrualRules(result.payload ?? []);
      }
    } catch (err) {
      setError(getApiErrorMessage(err, t, t('Failed to load accrual rules')));
    } finally {
      setIsLoading(false);
    }
  };

  const loadLeaveTypes = async () => {
    try {
      const result = await getCompanyLeaveTypes();
      if (!result?.errors?.length) {
        setLeaveTypesList(result.payload ?? []);
      }
    } catch {
      // leave types list remains empty; entitlements will show IDs
    }
  };

  const loadEligibleCount = async (filters?: {
    departmentIds?: string;
    branchIds?: string;
    employmentTypes?: string;
    minYearsOfService?: number;
    maxYearsOfService?: number;
    ftePercentage?: number;
  }) => {
    if (!currentPolicy?.id) return;
    try {
      const result = await getAccrualRuleEligibleCount(currentPolicy.id, filters);
      if (!result?.errors?.length) {
        setEligibleCount(result.payload ?? 0);
      }
    } catch {
      // non-critical - preview is informational
    }
  };

  const loadDepartments = async () => {
    if (!companyId) return;
    try {
      const result = await getDepartments(companyId);
      if (!result?.errors?.length) {
        setDepartmentsList(result.payload ?? []);
      }
    } catch {
      // non-critical
    }
  };

  const loadBranches = async () => {
    if (!companyId) return;
    try {
      const result = await getBranches(companyId);
      if (!result?.errors?.length) {
        setBranchesList(result.payload ?? []);
      }
    } catch {
      // non-critical
    }
  };

  const getLeaveTypeName = (leaveTypeId: number): string => {
    const lt = leaveTypesList.find(l => l.id === leaveTypeId);
    return lt?.name ?? `${t('Type')} ${leaveTypeId}`;
  };

  const getFrequencyLabel = (freq?: string | null): string => {
    if (!freq) return '-';
    const opt = frequencyOptions.find(o => o.value === freq);
    return opt?.label ?? freq;
  };

  /** Convert an AccrualRuleView to the local form state */
  const viewToFormState = (rule: AccrualRuleView): RuleFormState => ({
    id: rule.id,
    name: rule.name,
    roleLevels: rule.roleLevels ?? '',
    departmentIds: rule.departmentIds ?? '',
    branchIds: rule.branchIds ?? '',
    minYearsOfService: rule.minYearsOfService,
    maxYearsOfService: rule.maxYearsOfService,
    employmentTypes: rule.employmentTypes ?? '',
    ftePercentage: rule.ftePercentage,
    isActive: rule.isActive,
    probationRestricted: rule.probationRestricted ?? false,
    effectiveFrom: rule.effectiveFrom?.split('T')[0] ?? '',
    entitlements: rule.entitlements.map(e => ({
      leaveTypeId: e.leaveTypeId,
      entitlementDays: e.entitlementDays,
      accrualRate: e.accrualRate ?? '',
      accrualFrequency: (e.accrualFrequency as AccrualFrequency) ?? '',
      eligibilityMonths: e.eligibilityMonths,
      maxAccumulationCap: e.maxAccumulationCap ?? '',
      payTiers: (e.payTiers ?? []).map((pt: PayTierView) => ({
        fromDay: pt.fromDay,
        toDay: pt.toDay,
        payType: pt.payType
      }))
    }))
  });

  /** Convert the form state to an AccrualRuleInput for the API */
  const formStateToInput = (form: RuleFormState): AccrualRuleInput => ({
    name: form.name,
    roleLevels: form.roleLevels || undefined,
    departmentIds: form.departmentIds || undefined,
    branchIds: form.branchIds || undefined,
    minYearsOfService: form.minYearsOfService,
    maxYearsOfService: form.maxYearsOfService,
    employmentTypes: form.employmentTypes || undefined,
    ftePercentage: form.ftePercentage,
    isActive: form.isActive,
    probationRestricted: form.probationRestricted,
    effectiveFrom: form.effectiveFrom,
    entitlements: form.entitlements.map(e => ({
      leaveTypeId: e.leaveTypeId,
      entitlementDays: e.entitlementDays,
      accrualRate: e.accrualFrequency && e.accrualFrequency !== AccrualFrequency.Never ? computeRateDisplay(e.entitlementDays, e.accrualFrequency, t) : (e.accrualRate || undefined),
      accrualFrequency: e.accrualFrequency || undefined,
      eligibilityMonths: e.eligibilityMonths,
      maxAccumulationCap: e.maxAccumulationCap !== '' ? Number(e.maxAccumulationCap) : undefined,
      payTiers: e.payTiers
        .filter(pt => pt.payType !== '')
        .map(pt => ({
          fromDay: pt.fromDay,
          toDay: pt.toDay,
          payType: pt.payType as LeavePayType
        }))
    }))
  });

  const handleEditRule = (rule: AccrualRuleView): void => {
    setSaveError(null);
    setEditingRule(rule);
    setFormState(viewToFormState(rule));
    setShowRuleModal(true);
  };

  const handleDeleteRule = (id: number): void => {
    setDeleteConfirm({ open: true, ruleId: id });
  };

  const confirmDeleteRule = async (): Promise<void> => {
    if (!currentPolicy?.id || deleteConfirm.ruleId === null) return;
    const id = deleteConfirm.ruleId;
    const result = await deleteAccrualRule(currentPolicy.id, id);
    if (result?.errors?.length) {
      // CO-523: one surface owns EVERY failure of this action. A transport refusal (403/409/5xx)
      // already rejects out of deleteAccrualRule into the AlertDialog's inline error and holds the
      // dialog open; a 200-with-errors[] business refusal used to show a fading toast and CLOSE the
      // dialog instead. Throwing the Result routes it to the same inline error — classifyWriteError
      // reads Result.errors[] directly, so the Result itself is a valid error payload.
      throw result;
    } else {
      setAccrualRules(accrualRules.filter(r => r.id !== id));
      showToast(t('Accrual rule deleted successfully'), 'success');
    }
  };

  const haveTiersChanged = (): boolean => {
    if (!formState || !editingRule) return false;
    const originalEntitlements = editingRule.entitlements || [];
    return formState.entitlements.some((ent) => {
      const original = originalEntitlements.find(o => o.leaveTypeId === ent.leaveTypeId);
      if (!original) return ent.payTiers.length > 0;
      const origTiers = original.payTiers || [];
      if (origTiers.length !== ent.payTiers.length) return true;
      return ent.payTiers.some((pt, i) =>
        pt.fromDay !== origTiers[i]?.fromDay ||
        pt.toDay !== origTiers[i]?.toDay ||
        pt.payType !== origTiers[i]?.payType
      );
    });
  };

  const handleSaveRule = async (): Promise<void> => {
    if (!formState || !currentPolicy?.id) return;
    // Validate pay tiers for all entitlements
    for (const ent of formState.entitlements) {
      const tierError = validatePayTiers(ent.payTiers);
      if (tierError) {
        setSaveError(tierError);
        return;
      }
    }
    // Only warn if tiers actually changed compared to the loaded rule
    if (editingRule && haveTiersChanged()) {
      setTierWarningOpen(true);
      return;
    }
    await executeSave();
  };

  const executeSave = async (): Promise<void> => {
    if (!formState || !currentPolicy?.id) return;
    setIsSaving(true);
    try {
      const input = formStateToInput(formState);
      const result = editingRule
        ? await updateAccrualRule(editingRule.id, input)
        : await saveAccrualRule(currentPolicy.id, input);
      if (result?.errors?.length) {
        const msg = getApiErrorMessage(result, t);
        setSaveError(msg || t('translation:somethingWentWrong', 'Failed to save accrual rule'));
      } else {
        setSaveError(null);
        await loadAccrualRules();
        resetForm();
        showToast(editingRule ? t('Accrual rule updated successfully') : t('Accrual rule created successfully'), 'success');
      }
    } catch (err) {
      setSaveError(getApiErrorMessage(err, t, t('translation:somethingWentWrong', 'Failed to save accrual rule')));
    } finally {
      setIsSaving(false);
    }
  };

  const initializeNewRule = (): void => {
    setSaveError(null);
    setFormState({
      name: '',
      roleLevels: '',
      departmentIds: '',
      branchIds: '',
      minYearsOfService: 0,
      maxYearsOfService: 99,
      employmentTypes: '',
      ftePercentage: 100,
      isActive: true,
      probationRestricted: false,
      effectiveFrom: new Date().toISOString().split('T')[0],
      entitlements: [{ leaveTypeId: leaveTypesList[0]?.id ?? 1, entitlementDays: 21, accrualRate: '', accrualFrequency: AccrualFrequency.Monthly, eligibilityMonths: 0, maxAccumulationCap: '', payTiers: [] }]
    });
    setEditingRule(null);
    setShowRuleModal(true);
  };

  const resetForm = (): void => {
    setShowRuleModal(false);
    setEditingRule(null);
    setFormState(null);
  };

  const addEntitlementRow = (): void => {
    if (!formState) return;
    const usedIds = new Set(formState.entitlements.map(e => e.leaveTypeId));
    const availableType = leaveTypesList.find(lt => !usedIds.has(lt.id));
    if (!availableType) return;
    setFormState({
      ...formState,
      entitlements: [...formState.entitlements, { leaveTypeId: availableType.id, entitlementDays: 0, accrualRate: '', accrualFrequency: AccrualFrequency.Monthly, eligibilityMonths: 0, maxAccumulationCap: '', payTiers: [] }]
    });
  };

  const removeEntitlementRow = (index: number): void => {
    if (!formState) return;
    setFormState({
      ...formState,
      entitlements: formState.entitlements.filter((_, i) => i !== index)
    });
  };

  const updateEntitlement = (index: number, updates: Partial<EntitlementFormRow>): void => {
    if (!formState) return;
    const entitlements = [...formState.entitlements];
    entitlements[index] = { ...entitlements[index], ...updates };
    setFormState({ ...formState, entitlements });
  };

  const togglePayTiers = (index: number): void => {
    setExpandedPayTiers(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const addPayTierRow = (entitlementIndex: number): void => {
    if (!formState) return;
    const entitlements = [...formState.entitlements];
    const currentTiers = entitlements[entitlementIndex].payTiers;
    const lastToDay = currentTiers.length > 0 ? currentTiers[currentTiers.length - 1].toDay + 1 : 1;
    entitlements[entitlementIndex] = {
      ...entitlements[entitlementIndex],
      payTiers: [...currentTiers, { fromDay: lastToDay, toDay: lastToDay + 29, payType: LeavePayType.Paid }]
    };
    setFormState({ ...formState, entitlements });
  };

  const removePayTierRow = (entitlementIndex: number, tierIndex: number): void => {
    if (!formState) return;
    const entitlements = [...formState.entitlements];
    entitlements[entitlementIndex] = {
      ...entitlements[entitlementIndex],
      payTiers: entitlements[entitlementIndex].payTiers.filter((_, i) => i !== tierIndex)
    };
    setFormState({ ...formState, entitlements });
  };

  const updatePayTier = (entitlementIndex: number, tierIndex: number, updates: Partial<PayTierFormRow>): void => {
    if (!formState) return;
    const entitlements = [...formState.entitlements];
    const tiers = [...entitlements[entitlementIndex].payTiers];
    tiers[tierIndex] = { ...tiers[tierIndex], ...updates };
    entitlements[entitlementIndex] = { ...entitlements[entitlementIndex], payTiers: tiers };
    setFormState({ ...formState, entitlements });
  };

  /** Validate pay tiers: no overlapping ranges, fromDay >= 1, toDay >= fromDay */
  const validatePayTiers = (tiers: PayTierFormRow[]): string | null => {
    for (let i = 0; i < tiers.length; i++) {
      const tier = tiers[i];
      if (tier.fromDay < 1) return t('fromDayMustBeAtLeast1');
      if (tier.toDay < tier.fromDay) return t('toDayMustBeGreaterOrEqual');
      if (!tier.payType) return t('payTypeRequired');
      for (let j = i + 1; j < tiers.length; j++) {
        const other = tiers[j];
        if (tier.fromDay <= other.toDay && other.fromDay <= tier.toDay) {
          return t('tierRangesOverlap');
        }
      }
    }
    return null;
  };

  /** Parse comma-separated string to array for display */
  const parseCSV = (value?: string): string[] => {
    if (!value) return [];
    return value.split(',').map(s => s.trim()).filter(Boolean);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-foreground">{t('Entitlements & Accruals')}</h3>
          <p className="text-sm text-muted-foreground">{t('Configure tiered entitlement rules by role level and years of service')}</p>
        </div>
        <Button variant="default" iconName="Plus" onClick={initializeNewRule} data-testid="add-accrual-rule-btn">
          {t('Add Accrual Rule')}
        </Button>
      </div>

      <div className="bg-muted border border-border rounded-lg p-4">
        <div className="flex items-start space-x-3 rtl:space-x-reverse">
          <Icon name="Info" size={20} className="text-primary mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-1">{t('Entitlements Configuration')}</h3>
            <p className="text-sm text-foreground">
              {t('Define tiered rules for leave entitlements based on department, branch, years of service, and employment type. Each rule can specify per-type entitlement days and accrual settings.')}
            </p>
          </div>
        </div>
      </div>

      {currentPolicy && (
        <div className="bg-muted border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Icon name="Shield" size={16} className="text-primary" />
              <span className="text-sm font-semibold text-foreground">{t('Current Policy')}: {currentPolicy?.name}</span>
            </div>
            {eligibleCount !== null && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary/10 text-primary text-xs font-semibold rounded-full" data-testid="eligible-count-badge">
                <Icon name="Users" size={14} className="text-primary" />
                {t('This rule will apply to {{count}} eligible employees', { count: eligibleCount })}
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground">
            <span>{currentPolicy?.legalEntity} &bull; {currentPolicy?.country} &bull; {currentPolicy?.employmentType}</span>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center space-x-2 rtl:space-x-reverse">
            <Icon name="AlertCircle" size={16} className="text-destructive" />
            <span className="text-sm text-destructive">{error}</span>
          </div>
          <Button variant="outline" size="sm" onClick={loadAccrualRules} data-testid="leave-entitlements-retry">{t('Retry')}</Button>
        </div>
      )}

      {/* Accrual Rules List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : accrualRules.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="TrendingUp" size={48} className="text-muted-foreground/50 mx-auto mb-4" />
          <p className="text-muted-foreground">{t('No accrual rules configured yet.')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {accrualRules.map((rule) => (
            <div key={rule.id} className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow" data-testid="accrual-rule-card">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="flex items-center space-x-2 rtl:space-x-reverse">
                    <h4 className="text-md font-semibold text-foreground">{rule.name}</h4>
                    <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${rule.isActive ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'}`}>
                      {rule.isActive ? t('Active') : t('Inactive')}
                    </span>
                    {rule.probationRestricted && (
                      <span className="px-2 py-0.5 text-xs rounded-full font-medium bg-warning/10 text-warning">
                        {t('Probation Restricted')}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {t('Effective from')} {new Date(rule.effectiveFrom).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                  <Button variant="ghost" size="sm" iconName="Edit" onClick={() => handleEditRule(rule)} data-testid="leave-entitlements-edit-rule" />
                  <Button variant="ghost" size="sm" iconName="Trash2" onClick={() => handleDeleteRule(rule.id)} className="text-destructive" data-testid="leave-entitlements-delete-rule" />
                </div>
              </div>

              {/* Conditions */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-4 p-4 bg-muted rounded-lg">
                <div>
                  <div className="text-xs text-muted-foreground mb-1">{t('Departments')}</div>
                  <div className="flex flex-wrap gap-1">
                    {parseCSV(rule.departmentIds).length > 0 ? parseCSV(rule.departmentIds).map((id, i) => {
                      const dept = departmentsList.find(d => String(d.id) === id);
                      return <span key={i} className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded">{dept?.name ?? id}</span>;
                    }) : (
                      <span className="text-xs text-muted-foreground">{t('All')}</span>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground mb-1">{t('Branches')}</div>
                  <div className="flex flex-wrap gap-1">
                    {parseCSV(rule.branchIds).length > 0 ? parseCSV(rule.branchIds).map((id, i) => {
                      const branch = branchesList.find(b => String(b.id) === id);
                      return <span key={i} className="px-2 py-0.5 bg-success/10 text-success text-xs rounded">{branch?.name ?? id}</span>;
                    }) : (
                      <span className="text-xs text-muted-foreground">{t('All')}</span>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground mb-1">{t('Years of Service')}</div>
                  <span className="text-sm font-medium text-foreground">{rule.minYearsOfService} - {rule.maxYearsOfService} {t('years')}</span>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground mb-1">{t('Employment Type')}</div>
                  <div className="flex flex-wrap gap-1">
                    {parseCSV(rule.employmentTypes).length > 0 ? parseCSV(rule.employmentTypes).map((type, i) => (
                      <span key={i} className="px-2 py-0.5 bg-warning/10 text-warning text-xs rounded">{t(type)}</span>
                    )) : (
                      <span className="text-xs text-muted-foreground">{t('All')}</span>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground mb-1">{t('FTE %')}</div>
                  <span className="text-sm font-medium text-foreground">{rule.ftePercentage}%</span>
                </div>
              </div>

              {/* Entitlements Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-start py-2 text-muted-foreground font-medium">{t('Leave Type')}</th>
                      <th className="text-start py-2 text-muted-foreground font-medium">{t('Entitlement Days')}</th>
                      <th className="text-start py-2 text-muted-foreground font-medium">{t('Frequency')}</th>
                      <th className="text-start py-2 text-muted-foreground font-medium">{t('Accrual Rate')}</th>
                      <th className="text-start py-2 text-muted-foreground font-medium">{t('Eligibility')}</th>
                      <th className="text-start py-2 text-muted-foreground font-medium">{t('Max Accumulation Cap')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rule.entitlements?.map((ent, i) => (
                      <tr key={i} className="border-b border-border">
                        <td className="py-2 font-medium text-foreground">{ent.leaveTypeName || getLeaveTypeName(ent.leaveTypeId)}</td>
                        <td className="py-2 text-foreground">{ent.entitlementDays} {t('days')}</td>
                        <td className="py-2 text-foreground">{getFrequencyLabel(ent.accrualFrequency)}</td>
                        <td className="py-2 text-foreground">{ent.accrualRate || computeRateDisplay(ent.entitlementDays, ent.accrualFrequency, t) || '-'}</td>
                        <td className="py-2 text-foreground">{ent.eligibilityMonths > 0 ? `${ent.eligibilityMonths} ${t('months')}` : t('Immediate')}</td>
                        <td className="py-2 text-foreground">{ent.maxAccumulationCap ? `${ent.maxAccumulationCap} ${t('days')}` : <span className="text-muted-foreground">{t('Unlimited')}</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation */}
      <AlertDialog
        open={deleteConfirm.open}
        onOpenChange={(open) => setDeleteConfirm({ open, ruleId: open ? deleteConfirm.ruleId : null })}
        title={t('Delete Accrual Rule')}
        description={t('Are you sure you want to delete this item? This action cannot be undone.')}
        confirmLabel={t('Delete')}
        cancelLabel={t('Cancel')}
        variant="destructive"
        onConfirm={confirmDeleteRule}
      />

      {/* Tier Modification Warning */}
      <AlertDialog
        open={tierWarningOpen}
        onOpenChange={setTierWarningOpen}
        title={t('Pay Tiers Modified')}
        description={t('tierModificationWarning')}
        confirmLabel={t('Continue')}
        cancelLabel={t('Cancel')}
        variant="warning"
        onConfirm={executeSave}
        isLoading={isSaving}
      />

      <ToastRenderer toast={toast} onDismiss={dismissToast} />

      {/* Add/Edit Rule Modal */}
      <Dialog open={showRuleModal && !!formState} onOpenChange={(open) => { if (!open) resetForm(); }}>
        <DialogContent size="xl">
          <DialogHeader>
            <DialogTitle>
              {editingRule ? t('Edit Accrual Rule') : t('Add Accrual Rule')}
            </DialogTitle>
          </DialogHeader>

          {formState && (
            <>
              <DialogBody className="space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">{t('Rule Name')} *</label>
                    <Input
                      type="text"
                      value={formState.name}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormState({ ...formState, name: e.target.value })}
                      placeholder={t('e.g., Standard Entitlement')}
                      data-testid="rule-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">{t('Effective From')}</label>
                    <Input
                      type="date"
                      value={formState.effectiveFrom}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormState({ ...formState, effectiveFrom: e.target.value })}
                      data-testid="rule-effective-from-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">{t('Status')}</label>
                    <Select
                      value={formState.isActive ? 'active' : 'inactive'}
                      onChange={(value: string) => setFormState({ ...formState, isActive: value === 'active' })}
                      options={[
                        { value: 'active', label: t('Active') },
                        { value: 'inactive', label: t('Inactive') }
                      ]}
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="probationRestricted"
                    checked={formState.probationRestricted}
                    onChange={(e) => setFormState({ ...formState, probationRestricted: e.target.checked })}
                    className="h-4 w-4 rounded border-border text-primary focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    data-testid="rule-probation-restricted"
                  />
                  <label htmlFor="probationRestricted" className="text-sm font-medium text-foreground">
                    {t('Restrict during probation')}
                  </label>
                  <span className="text-xs text-muted-foreground">{t('Accrual paused until employee probation ends')}</span>
                </div>

                {/* Conditions */}
                <div className="p-4 bg-muted rounded-lg border border-border">
                  <h4 className="text-md font-semibold text-foreground mb-4">{t('Conditions')}</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">{t('Departments')}</label>
                      <Select
                        value={parseCSV(formState.departmentIds)}
                        onChange={(value: string[]) => {
                          const newDeptIds = value.join(', ');
                          setFormState({ ...formState, departmentIds: newDeptIds });
                          loadEligibleCount({ departmentIds: newDeptIds, branchIds: formState.branchIds, employmentTypes: formState.employmentTypes, minYearsOfService: formState.minYearsOfService, maxYearsOfService: formState.maxYearsOfService, ftePercentage: formState.ftePercentage });
                        }}
                        options={departmentsList.map(d => ({ value: String(d.id), label: d.name }))}
                        multiple
                        placeholder={t('All departments')}
                        data-testid="rule-departments-select"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">{t('Branches')}</label>
                      <Select
                        value={parseCSV(formState.branchIds)}
                        onChange={(value: string[]) => {
                          const newBranchIds = value.join(', ');
                          setFormState({ ...formState, branchIds: newBranchIds });
                          loadEligibleCount({ departmentIds: formState.departmentIds, branchIds: newBranchIds, employmentTypes: formState.employmentTypes, minYearsOfService: formState.minYearsOfService, maxYearsOfService: formState.maxYearsOfService, ftePercentage: formState.ftePercentage });
                        }}
                        options={branchesList.map(b => ({ value: String(b.id), label: b.name }))}
                        multiple
                        placeholder={t('All branches')}
                        data-testid="rule-branches-select"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">{t('Employment Types')}</label>
                      <Select
                        value={parseCSV(formState.employmentTypes)}
                        onChange={(value: string[]) => {
                          const newTypes = value.join(', ');
                          setFormState({ ...formState, employmentTypes: newTypes });
                          loadEligibleCount({ departmentIds: formState.departmentIds, branchIds: formState.branchIds, employmentTypes: newTypes, minYearsOfService: formState.minYearsOfService, maxYearsOfService: formState.maxYearsOfService, ftePercentage: formState.ftePercentage });
                        }}
                        options={employmentTypeOptions}
                        multiple
                        placeholder={t('All employment types')}
                        data-testid="rule-employment-types-select"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">{t('FTE Percentage')}</label>
                      <Input
                        type="number"
                        value={formState.ftePercentage}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormState({
                          ...formState,
                          ftePercentage: parseInt(e.target.value) || 100
                        })}
                        min="0" max="100"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">{t('Min Years of Service')}</label>
                      <Input
                        type="number"
                        value={formState.minYearsOfService}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormState({
                          ...formState,
                          minYearsOfService: parseInt(e.target.value) || 0
                        })}
                        min="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">{t('Max Years of Service')}</label>
                      <Input
                        type="number"
                        value={formState.maxYearsOfService}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormState({
                          ...formState,
                          maxYearsOfService: parseInt(e.target.value) || 99
                        })}
                        min="0"
                      />
                    </div>
                  </div>
                </div>

                {/* Entitlements per Leave Type */}
                <div className="p-4 bg-success/10 rounded-lg border border-success/20">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-md font-semibold text-foreground">{t('Per-Type Entitlements')}</h4>
                    <Button variant="outline" size="sm" iconName="Plus" onClick={addEntitlementRow} disabled={formState && formState.entitlements.length >= leaveTypesList.length} data-testid="add-entitlement-btn">
                      {t('Add Entitlement')}
                    </Button>
                  </div>

                  <div className="space-y-3">
                    {formState.entitlements.map((ent, index) => (
                      <div key={index} className="p-3 bg-card rounded-lg border border-border" data-testid="entitlement-row">
                        <div className={`grid grid-cols-2 md:grid-cols-4 gap-3 items-end ${ent.accrualFrequency !== AccrualFrequency.Never ? 'lg:grid-cols-7' : 'lg:grid-cols-4'}`}>
                          <div>
                            <label className="block text-xs font-medium text-foreground mb-1">{t('Leave Type')}</label>
                            <Select
                              value={ent.leaveTypeId}
                              onChange={(value: number) => updateEntitlement(index, { leaveTypeId: value })}
                              options={leaveTypesList
                                .filter(lt => lt.id === ent.leaveTypeId || !formState.entitlements.some((other, i) => i !== index && other.leaveTypeId === lt.id))
                                .map(lt => ({ value: lt.id, label: lt.name }))}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-foreground mb-1">{t('Entitlement Days')}</label>
                            <Input
                              type="number"
                              value={ent.entitlementDays}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateEntitlement(index, { entitlementDays: parseFloat(e.target.value) || 0 })}
                              min="0" step="0.5"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-foreground mb-1">{t('Frequency')}</label>
                            <Select
                              value={ent.accrualFrequency}
                              onChange={(value: string) => updateEntitlement(index, { accrualFrequency: value as AccrualFrequency })}
                              options={frequencyOptions}
                              placeholder={t('Select frequency')}
                            />
                          </div>
                          {ent.accrualFrequency !== AccrualFrequency.Never && (
                            <>
                              <div>
                                <label className="block text-xs font-medium text-foreground mb-1">{t('Accrual Rate')}</label>
                                <div className="text-sm text-foreground py-2 px-3 bg-muted rounded border border-border min-h-[38px] flex items-center" data-testid="computed-rate-display">
                                  {computeRateDisplay(ent.entitlementDays, ent.accrualFrequency, t) || <span className="text-muted-foreground">-</span>}
                                </div>
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-foreground mb-1">{t('Eligibility (months)')}</label>
                                <Input
                                  type="number"
                                  value={ent.eligibilityMonths}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateEntitlement(index, { eligibilityMonths: parseInt(e.target.value) || 0 })}
                                  min="0"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-foreground mb-1">{t('Max Accumulation Cap')}</label>
                                <Input
                                  type="number"
                                  value={ent.maxAccumulationCap}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateEntitlement(index, { maxAccumulationCap: e.target.value ? parseFloat(e.target.value) : '' })}
                                  min="0" step="0.5"
                                  placeholder={t('Unlimited')}
                                  data-testid="entitlement-max-cap"
                                />
                              </div>
                            </>
                          )}
                          <div>
                            <Button variant="ghost" size="sm" iconName="X" onClick={() => removeEntitlementRow(index)} className="text-destructive" data-testid="leave-entitlements-remove-row" />
                          </div>
                        </div>

                        {/* Pay Tiers Sub-section */}
                        <div className="mt-3 pt-3 border-t border-border">
                          <button
                            type="button"
                            onClick={() => togglePayTiers(index)}
                            className="flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                            data-testid="toggle-pay-tiers-btn"
                          >
                            <Icon name={expandedPayTiers.has(index) ? 'ChevronDown' : 'ChevronRight'} size={16} />
                            {t('payTiers')}
                            {ent.payTiers.length > 0 && (
                              <span className="px-1.5 py-0.5 text-xs bg-primary/10 text-primary rounded-full">{ent.payTiers.length}</span>
                            )}
                          </button>

                          {expandedPayTiers.has(index) && (
                            <div className="mt-3 space-y-3" data-testid="pay-tiers-section">
                              {ent.payTiers.length === 0 ? (
                                <p className="text-sm text-muted-foreground italic">{t('fullyPaidDefault')}</p>
                              ) : (
                                <div className="overflow-x-auto">
                                  <table className="w-full text-sm">
                                    <thead>
                                      <tr className="border-b border-border">
                                        <th className="text-start py-2 text-muted-foreground font-medium text-xs">{t('From Day')}</th>
                                        <th className="text-start py-2 text-muted-foreground font-medium text-xs">{t('To Day')}</th>
                                        <th className="text-start py-2 text-muted-foreground font-medium text-xs">{t('Pay Type')}</th>
                                        <th className="py-2 w-10"></th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {ent.payTiers.map((tier, tierIndex) => (
                                        <tr key={tierIndex} className="border-b border-border" data-testid="pay-tier-row">
                                          <td className="py-2 pe-2">
                                            <Input
                                              type="number"
                                              value={tier.fromDay}
                                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => updatePayTier(index, tierIndex, { fromDay: parseInt(e.target.value) || 1 })}
                                              min="1"
                                              data-testid="pay-tier-from-day"
                                            />
                                          </td>
                                          <td className="py-2 pe-2">
                                            <Input
                                              type="number"
                                              value={tier.toDay}
                                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => updatePayTier(index, tierIndex, { toDay: parseInt(e.target.value) || 1 })}
                                              min="1"
                                              data-testid="pay-tier-to-day"
                                            />
                                          </td>
                                          <td className="py-2 pe-2">
                                            <Select
                                              value={tier.payType}
                                              onChange={(value: string) => updatePayTier(index, tierIndex, { payType: value as LeavePayType })}
                                              options={payTypeOptions}
                                              placeholder={t('Select pay type')}
                                              data-testid="pay-tier-pay-type"
                                            />
                                          </td>
                                          <td className="py-2">
                                            <Button variant="ghost" size="sm" iconName="X" onClick={() => removePayTierRow(index, tierIndex)} className="text-destructive" data-testid="remove-pay-tier-btn" />
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                              <Button variant="outline" size="sm" iconName="Plus" onClick={() => addPayTierRow(index)} data-testid="add-pay-tier-btn">
                                {t('Add Tier')}
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </DialogBody>

              <DialogFooter className="flex-col gap-3">
                {saveError && (
                  <div className="flex items-center gap-2 text-sm text-destructive w-full">
                    <Icon name="AlertCircle" size={16} />
                    <span>{saveError}</span>
                  </div>
                )}
                <div className="flex items-center justify-end space-x-3 rtl:space-x-reverse w-full">
                  <Button variant="outline" onClick={resetForm} data-testid="leave-entitlements-modal-cancel">{t('Cancel')}</Button>
                  <Button
                    variant="default"
                    onClick={handleSaveRule}
                    disabled={!formState.name || isSaving}
                    data-testid="accrual-rule-save-btn"
                  >
                    {isSaving ? t('Saving...') : `${editingRule ? t('Update') : t('Add')} ${t('Rule')}`}
                  </Button>
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EntitlementsAccrualsTab;
